import { useState } from 'react';
import type { CompanyStageStatus, StageStatusResurfaceMode } from '@/lib/types';
import { Button, Field, Input, Modal, Textarea } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import {
  FOLLOW_UP_QUICK_PICKS,
  addMonthsIso,
  nextCycleFollowUpDate,
  nextDayIso,
  todayUtcIsoDate,
} from '@/lib/stage-status-follow-up';

/*
 * StageStatusFollowUpDialog — the extra input a company sub-stage asks for
 * before it can be saved (spec 2026-10-23, owner decisions 2, 3 and 7).
 *
 * Behaviour comes from the status's FLAGS, never its name (statuses are
 * admin-renamable):
 *   - `resurfaceMode: 'date'`        → a required follow-up date (quick picks
 *                                      +3/+6/+9/+12 months, or a date input).
 *   - `resurfaceMode: 'next_cycle'`  → the computed next 1 March, editable.
 *   - `requiresReason`               → a required reason, trimmed 1..1000.
 *   - `suppressesMarketing`          → one destructive-consequence sentence.
 *
 * The date window mirrors the server (`FOLLOW_UP_DATE_INVALID`): strictly after
 * today and no later than today + 3 years. The server is still the boundary;
 * this only keeps Confirm honest.
 *
 * State is reset on every open by mounting the body only while open (keyed on
 * the status), so a reopened dialog never shows the previous company's reason.
 */

const REASON_MAX = 1000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface StageStatusFollowUpDialogProps {
  open: boolean;
  companyName: string;
  status: CompanyStageStatus;
  initialFollowUpOn?: string | null;
  /**
   * The resurfaceMode of the company's CURRENT status (before this change).
   * `initialFollowUpOn` pre-fills only when this equals the new status's mode
   * and the date is tomorrow (UTC) or later. `undefined` = unknown: no pre-fill.
   */
  currentResurfaceMode?: StageStatusResurfaceMode | null;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (v: { followUpOn: string | null; stageStatusReason: string | null }) => void;
}

export function StageStatusFollowUpDialog(props: StageStatusFollowUpDialogProps) {
  if (!props.open) return null;
  return <DialogBody key={props.status.id} {...props} />;
}

function DialogBody({
  companyName,
  status,
  initialFollowUpOn,
  currentResurfaceMode,
  busy = false,
  error,
  onCancel,
  onConfirm,
}: StageStatusFollowUpDialogProps) {
  const resurfaceMode = status.resurfaceMode ?? null;
  const requiresReason = status.requiresReason === true;
  const suppressesMarketing = status.suppressesMarketing === true;

  // UTC today, as the server's FOLLOW_UP_DATE_INVALID check and next-cycle default use.
  const [today] = useState(() => todayUtcIsoDate());
  const minDate = nextDayIso(today);
  const maxDate = addMonthsIso(today, 36);

  // The stored date is kept only when it belongs to the same kind of wait
  // (current status has the same resurfaceMode) and is still a valid future
  // date. Otherwise a "date" stored for one status would be offered for another
  // (Near Future 2027-05-01 → LOB must show the next 1 March), and a stored
  // today/past date would open the dialog already in error. An unknown current
  // mode (`undefined`, caller not passing it) never pre-fills.
  const [followUpOn, setFollowUpOn] = useState<string>(() => {
    const keepStored =
      resurfaceMode !== null &&
      currentResurfaceMode === resurfaceMode &&
      typeof initialFollowUpOn === 'string' &&
      ISO_DATE.test(initialFollowUpOn) &&
      initialFollowUpOn >= minDate;
    if (keepStored) return initialFollowUpOn;
    if (resurfaceMode === 'next_cycle') return nextCycleFollowUpDate(today);
    return '';
  });
  const [reason, setReason] = useState('');

  const dateWellFormed = ISO_DATE.test(followUpOn);
  const dateInRange = dateWellFormed && followUpOn >= minDate && followUpOn <= maxDate;
  const trimmedReason = reason.trim();

  let blockedBy: string | null = null;
  if (resurfaceMode !== null && followUpOn === '') blockedBy = 'Choose a follow-up date';
  else if (resurfaceMode !== null && !dateInRange)
    blockedBy = 'The follow-up date must be after today and within 3 years';
  else if (requiresReason && trimmedReason === '') blockedBy = 'Enter a reason';

  const dateError =
    resurfaceMode !== null && followUpOn !== '' && !dateInRange
      ? 'Choose a date after today and within 3 years.'
      : undefined;

  const close = () => {
    if (!busy) onCancel();
  };

  const confirm = () => {
    if (blockedBy !== null || busy) return;
    onConfirm({
      followUpOn: resurfaceMode !== null ? followUpOn : null,
      stageStatusReason: requiresReason ? trimmedReason : null,
    });
  };

  return (
    <Modal
      open
      onClose={close}
      title={`${status.name} · ${companyName}`}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirm}
            loading={busy}
            disabled={blockedBy !== null}
            tooltip={blockedBy ?? undefined}
          >
            Confirm
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          confirm();
        }}
      >
        {resurfaceMode !== null && (
          <Field label="Follow-up date" required error={dateError}>
            {(fieldProps) => (
              <div className="grid gap-2">
                {resurfaceMode === 'date' && (
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Quick picks">
                    {FOLLOW_UP_QUICK_PICKS.map((pick) => {
                      const value = addMonthsIso(today, pick.months);
                      const active = followUpOn === value;
                      return (
                        <Button
                          key={pick.months}
                          type="button"
                          variant={active ? 'primary' : 'secondary'}
                          aria-pressed={active}
                          disabled={busy}
                          onClick={() => setFollowUpOn(value)}
                        >
                          {pick.label}
                        </Button>
                      );
                    })}
                  </div>
                )}
                <Input
                  {...fieldProps}
                  type="date"
                  value={followUpOn}
                  min={minDate}
                  max={maxDate}
                  required
                  disabled={busy}
                  invalid={dateError !== undefined}
                  onChange={(event) => setFollowUpOn(event.target.value)}
                />
              </div>
            )}
          </Field>
        )}

        {requiresReason && (
          <Field label="Reason" required>
            {(fieldProps) => (
              <Textarea
                {...fieldProps}
                value={reason}
                maxLength={REASON_MAX}
                required
                disabled={busy}
                autoGrow={{ minRows: 3, maxRows: 8 }}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
        )}

        {suppressesMarketing && (
          <Notice tone="warning">Its contacts will stop receiving marketing emails.</Notice>
        )}

        {error && (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        )}
      </form>
    </Modal>
  );
}
